import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  makeStyles,
  Grid,
  Button,
  Box,
  Checkbox,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import {
  generateAlias,
  groupTranchesByDate,
  countIdActiviteMatches,
} from "./utils";
import UserDetails from "./UserDetails";
import ModalAttributionTranche from "./modal/ModalAttributionTranche";
import ButtonStyled from "../../components/general/ButtonStyled";
import ModalReatribution from "./modal/ModalReatribution";
import PopperTipsBenevol from "../../components/cards/PopperTipBenev";
import GetRandomKey from "../../components/general/genereRandomKey";
import { dicoColorLevel } from "../../components/onglets/OngletRemarque";
import { useStylesGlobal } from "../GlobalStyle.css";
import AppContext from "../../components/appContext";
import UserAvatar from "../../components/Avatar/UserAvatar";

const useStyles = makeStyles((theme) => ({
  activityText: {
    width: "100%",
    display: "inline-block",
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.secondary.main,
    },
  },
  selectedActivity: {
    // padding: theme.spacing(1), // Espacement interne
    fontWeight: 700,
  },
  notSelectedActivity: {
    color: "",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "calc(100% - 70px)",
  },
  colorBox: {
    display: "inline-block",
    width: 12,
    height: 12,
    marginRight: 8,
    border: "1px solid #000",
    position: "relative",
  },
  lineThrough: {
    position: "absolute",
    top: "calc(50% - 0px)",
    left: 0,
    width: "15px",
    height: 1,
    backgroundColor: "#000",
    transform: "translateY(calc(-50% - 0px)) translateX(-2px)",
  },
  remarqueContainer: {
    display: "flex",
    alignItems: "center",
    textOverflow: "ellipsis",
    marginRight: "10px",
    paddingLeft: "8px",
    width: "calc(100% - 30%)",
  },
  remarqueText: {
    flex: 1,
    textAlign: "left",
    textWrap: "wrap",
    wordWrap: "break-word",
    maxWidth: "calc(100% - 10px)",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
}));

const LegendTooltip = () => {
  const classes = useStyles();
  return (
    <Tooltip
      title={
        <React.Fragment>
          <div>
            <div
              className={classes.colorBox}
              style={{ backgroundColor: "#1976d2" }}
            ></div>
            <Typography
              variant="body2"
              color="inherit"
              style={{ display: "inline-block" }}
            >
              Ajouté / Réattribué
            </Typography>
          </div>
          <div>
            <div
              className={classes.colorBox}
              style={{ backgroundColor: "transparent" }}
            >
              <div className={classes.lineThrough} />
            </div>
            <Typography
              variant="body2"
              color="inherit"
              style={{ display: "inline-block" }}
            >
              Refusé / Non Attribué
            </Typography>
          </div>
        </React.Fragment>
      }
      arrow
    >
      <IconButton>
        <InfoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const updateTranches = (activity, wishTranches, tempo, setHasTranche) => {
  let filteredDates;
  if (activity) {
    filteredDates = wishTranches?.filter(
      (tranche) => tranche.parentId === activity?.id
    );
  } else {
    filteredDates = wishTranches;
  }

  // Cross-check with tempo and add 'selected' or 'add' attributes
  const updatedFilteredDates = filteredDates.map((tranche) => {
    const matchedTempo = tempo.find((temp) => temp.ID === tranche.ID);
    if (matchedTempo) {
      return { ...tranche, selected: true };
    }
    return tranche;
  });

  // Add new tranches from tempo that are not in wish
  const additionalTranches = tempo
    .filter(
      (temp) =>
        temp.parentId === activity?.id &&
        !wishTranches?.some((tranche) => tranche.ID === temp.ID)
    )
    .map((temp) => ({ ...temp, add: true, selected: true }));

  const comcateTranche = [...updatedFilteredDates, ...additionalTranches];

  setHasTranche(comcateTranche.some((t) => t.selected));

  const updatedComcateTranche = comcateTranche.map((tranche) => {
    return { ...tranche, treated: activity.treated };
  });

  return updatedComcateTranche;
};

const UserCard = ({
  setReload,
  user,
  activeActivity,
  activeStep,
  chefOnIds,
  distinctParent,
  handleCheckboxChange,
}) => {
  const [selectedActivity, setSelectedActivity] = useState(
    activeActivity || ""
  );
  const [filteredDates, setFilteredDates] = useState(user.wish?.tranches);

  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [traitable, setTraitable] = useState(true);
  const [openRea, setOpenRea] = useState(false);
  const [selectedTranches, setSelectedTranches] = useState([]);
  const [remarque, setRemarque] = useState("");
  const [hasTranche, setHasTranche] = useState(false);
  const [isListeAttente, setIsListeAttente] = useState(
    activeStep.type === "listeAttente"
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenRea = () => setOpenRea(true);
  const handleCloseRea = () => setOpenRea(false);

  const handelReatribution = () => handleOpenRea();
  const myContext = useContext(AppContext);
  const openSidebar = myContext.openSidebar;

  const [hoveredPerson, setHoveredPerson] = useState(null);

  const stylesGlobal = useStylesGlobal();
  useEffect(() => {
    const person = {
      Person_ID: user.ID,
      Person_Name: user.Name,
      Person_Firstname: user.Firstname,
      Person_Birth: user.Birth,
      Person_Phone_Mobile: user.Phone_Mobile,
      Person_Email: user.Email,
      Person_Address: user.Address,
      Person_Tshirt: user.Tshirt,
      Person_InfosComples: user.InfosComplementaires,
      Person_LastInscription: user.lastInscription,
      Person_Profile: user.Person_Profile,
      Person_Remarque: user.Remarque,
      Person_RemarqueLevel: user.RemarqueLevel,
    };
    setHoveredPerson(person);
    const remarque = user?.wish?.remarques?.RemarqueGlobale;
    setRemarque(remarque);
  }, [user]);

  useEffect(() => {
    setSelectedActivity(activeActivity);
  }, [activeActivity]);

  const getFiteredDate = useCallback(
    (activity) => {
      const wishTranches = user.wish?.tranches || [];
      const tempo = user.tempo || [];
      const newFilteredDates = updateTranches(
        activity,
        wishTranches,
        tempo,
        setHasTranche
      );
      setFilteredDates(newFilteredDates);
    },
    [user]
  );

  useEffect(() => {
    getFiteredDate(selectedActivity);

    const areAllOccurrencesTreated = (activityName, activities) => {
      if (!activities || activities.length === 0) {
        console.log("No activities found.");
        return false;
      }
      const filteredActivities = activities.filter(
        (activity) => activity.nom === activityName
      );
      if (filteredActivities.length === 0) return false;
      return filteredActivities.every((activity) => activity.treated);
    };

    const allOccurrencesTreated = areAllOccurrencesTreated(
      selectedActivity?.name,
      user.wish?.ChoixActivites
    );

    setTraitable(!allOccurrencesTreated);
  }, [selectedActivity, user, getFiteredDate]);

  const handleActivityChange = (event) => {
    let activite = distinctParent.find(
      (activite) => activite.id === event.target.value
    );
    if (!activite) {
      activite = chefOnIds.find(
        (activite) => activite.ID === event.target.value
      );
      if (activite) activite.id = event.target.value;
    }
    setSelectedActivity(activite);
  };

  const handelMousseEnter = (activite) => {
    if (activeStep.type === "listeAttente") setIsListeAttente(false);
    getFiteredDate(activite);
  };
  const handelMousseLeave = (activite) => {
    const act = activite ? activite : selectedActivity; // pour que les treated soit present malgré les array différant
    if (activeStep.type === "listeAttente") setIsListeAttente(true);
    getFiteredDate(act);
  };
  const beforeClosingFiche = () => {
    setReload((r) => !r);
  };

  return (
    <Card
      style={{
        marginBottom: 20,
        position: "relative",
        overflow: "visible",
        maxWidth: !openSidebar ? "calc(100vw - 0px)" : "calc(100vw - 300px)",
      }}
    >
      <CardHeader
        avatar={
          <div
            className={
              user.RemarqueLevel !== null && user.RemarqueLevel > -1
                ? stylesGlobal[dicoColorLevel[user.RemarqueLevel]]
                : ""
            }
            style={{
              width: 108,
              height: 108,
              padding: 4,
              borderRadius: "50%",
              background:
                user.RemarqueLevel !== null && user.RemarqueLevel > -1
                  ? ""
                  : "#eee",
              textAlign: "center",
            }}
          >
            <UserAvatar key={user.ID} user={user} />
          </div>
        }
        title={
          <Box className={classes.headerContainer} component="div">
            {hoveredPerson && (
              <PopperTipsBenevol
                idBenevole={user.ID}
                person={hoveredPerson}
                text={`${user.Firstname} ${user.Name}`}
                componentKey={GetRandomKey(9)}
                showEdit={true}
                position="bottom"
                styleButon={{ fontSize: "1.25rem", fontWeight: "500" }}
                beforeClosingFiche={beforeClosingFiche}
              />
            )}
            {activeStep?.type === "preValider" && (
              <Checkbox
                onChange={() => handleCheckboxChange(user.ID)}
                color="primary"
              />
            )}
          </Box>
        }
        titleTypographyProps={{ component: "div" }}
        subheader={<UserDetails user={user} />}
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <Typography variant="h6" style={{ marginBottom: 3 }}>
              Dates et horaires
              <LegendTooltip />
            </Typography>
            <div
              style={{
                maxHeight: 150,
                height: 150,
                minHeight: 150,
                overflow: "auto",
              }}
            >
              {!isListeAttente &&
                groupTranchesByDate(filteredDates)?.map(([date, tranches]) => {
                  // const activityIsTreated = user.wish?.ChoixActivites?.some((a) => a.id === tranches[0].parentId && a.treated);
                  return (
                    <div key={date} style={{ marginBottom: 10 }}>
                      <Typography
                        variant="body1"
                        style={{
                          fontWeight: "bold",
                          display: "inline-block",
                          width: "150px",
                        }}
                      >
                        {date}
                      </Typography>
                      {tranches?.map((tranche) => {
                        const activityIsTreated = tranche.treated || false;
                        const choisi = tranche.selected || false;
                        const add = tranche.add || false;
                        return (
                          <Typography
                            variant="body2"
                            key={tranche.ID}
                            style={{
                              marginRight: 10,
                              display: "inline-block",
                              width: "90px",
                              color: add ? "#1976d2" : "",
                              textDecoration:
                                activityIsTreated || (hasTranche && !choisi)
                                  ? "line-through"
                                  : "none",
                              opacity:
                                activityIsTreated || (hasTranche && !choisi)
                                  ? "0.35"
                                  : "1",
                            }}
                          >
                            {tranche.Alias ||
                              generateAlias(
                                tranche.HeureDebut,
                                tranche.HeureFin
                              )}
                          </Typography>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6">Activités & expérience</Typography>
            {user.wish?.ChoixActivites.map((currentAct, index) => {
              if (currentAct !== undefined) {
                const activity = distinctParent.find(
                  (item) => item.id === currentAct.id
                );
                const nbHistorique = countIdActiviteMatches(
                  user?.Historique?.Historique,
                  activity?.id
                );
                return (
                  <div
                    key={index}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      margin: "8px 0",
                    }}
                    onMouseEnter={() => handelMousseEnter(currentAct)}
                    onMouseLeave={() =>
                      selectedActivity?.id === currentAct.id &&
                      !currentAct?.treated // pour que les treated soient present malgré les array différant
                        ? handelMousseLeave(currentAct)
                        : handelMousseLeave()
                    }
                  >
                    <Typography
                      // onClick={() => activityChef && !currentAct?.treated && setSelectedActivity(activity)}
                      className={`${
                        !currentAct?.treated ? classes.activityText : ""
                      } 
                                                ${
                                                  selectedActivity?.id ===
                                                  activity?.id
                                                    ? classes.selectedActivity
                                                    : classes.notSelectedActivity
                                                }`}
                      style={{
                        textDecoration: currentAct?.treated
                          ? "line-through"
                          : "none",
                        opacity: currentAct?.treated ? "0.35" : "1",
                      }}
                    >
                      {index + 1}.{" "}
                      <span
                        style={{
                          display: "inline-block",
                          width: "calc(100% -  30px)",
                          textDecoration: currentAct?.treated
                            ? "line-through"
                            : "none",
                        }}
                      >
                        <span
                          style={{
                            color: currentAct.reattribuer ? "#1976d2" : "",
                          }}
                        >
                          {activity?.nom}
                          <span style={{ fontSize: "14px" }}>
                            {nbHistorique.count > 0
                              ? " (" +
                                nbHistorique.count +
                                "x - " +
                                nbHistorique.totalDuration.replace(":", "h") +
                                ")"
                              : " (0)"}
                          </span>
                        </span>
                      </span>
                    </Typography>
                  </div>
                );
              }
              return "";
            })}
          </Grid>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{ justifyContent: "flex-end", width: "100%" }}
          >
            {remarque && remarque !== "" && (
              <Box className={classes.remarqueContainer}>
                <Typography
                  className={classes.remarqueText}
                  title={remarque}
                  component="p"
                >
                  Remarque : {remarque}
                </Typography>
              </Box>
            )}
            <Box className={classes.buttonContainer}>
              {((chefOnIds && chefOnIds.length > 1) || isListeAttente) && (
                <Button
                  onClick={() => handelReatribution()}
                  variant="contained"
                  color="primary"
                  style={{ marginRight: 10 }}
                >
                  REATTRIBUTION
                </Button>
              )}
              {activeStep?.type === "wish" && traitable && (
                <ButtonStyled
                  onClick={() => handleOpen()}
                  variant="contained"
                  color="errorBtn"
                >
                  TRAITER
                </ButtonStyled>
              )}
              {activeStep?.type === "preValider" && (
                <ButtonStyled
                  onClick={() => handleOpen()}
                  variant="contained"
                  color="warningBtn"
                >
                  FINALISER
                </ButtonStyled>
              )}
              {activeStep?.type === "valide" && (
                <ButtonStyled
                  onClick={() => handleOpen()}
                  variant="contained"
                  color="grisBtn"
                >
                  MODIFIER
                </ButtonStyled>
              )}
            </Box>
          </Box>
        </Grid>
        {open && (
          <ModalAttributionTranche
            setReload={setReload}
            user={user}
            open={open}
            handleClose={handleClose}
            selectedActivity={selectedActivity}
            selectedTranches={selectedTranches}
            setSelectedTranches={setSelectedTranches}
            activeStep={activeStep}
          />
        )}
        {openRea && (
          <ModalReatribution
            setReload={setReload}
            user={user}
            open={openRea}
            handleClose={handleCloseRea}
            selectedActivity={selectedActivity}
            chefOnIds={chefOnIds}
            handleActivityChange={handleActivityChange}
            ChoixActivites={user.wish?.ChoixActivites || []}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default UserCard;
